import React, { useCallback, useEffect, useRef, useState } from "react";
import FullScreenIcon from "../../assets/icons/broadcast-fullscreen.svg";
import PlayIcon from "../../assets/icons/replay-play.svg";
import { IAgoraTrack } from "../../helpers/types";
import "./index.less";

interface props extends IAgoraTrack {
  isBlur?: boolean;
  index?: number;
  sessionId?: string;
  isFromMobile: boolean;
  mirror?: boolean;
  objectFit?: "cover" | "contain";
  showControls?: boolean;
  onControlsVisibilityChange?: (visible: boolean) => void;
}

const BroadCastViPlayer: React.FC<props> = ({
  tracks,
  isBlur,
  index,
  isFromMobile,
  mirror,
  objectFit = "cover",
  showControls = false,
  onControlsVisibilityChange,
}) => {
  const playerRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const tracksRef = useRef<props["tracks"]>([]);
  const [needsResume, setNeedsResume] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(true);
  const [isFullscreenActive, setIsFullscreenActive] = useState<boolean>(false);
  const [controlsVisible, setControlsVisible] = useState<boolean>(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const resumeStream = useCallback((fit: string) => {
    const videoEl = playerRef.current;
    if (!videoEl || !tracksRef.current?.length) return;
    const freshTracks = tracksRef.current.map((t) => t.getMediaStreamTrack());
    const freshStream = new MediaStream(freshTracks);
    streamRef.current = freshStream;
    videoEl.srcObject = freshStream;
    videoEl.style.setProperty("object-fit", fit, "important");

    const onPlaying = () => {
      clearTimeout(playingTimer);
      setNeedsResume(false);
    };
    // If 'playing' doesn't fire within 2s, frames aren't rendering — keep overlay
    const playingTimer = setTimeout(() => {
      videoEl.removeEventListener("playing", onPlaying);
      setNeedsResume(true);
    }, 2000);
    videoEl.addEventListener("playing", onPlaying, { once: true });

    videoEl.play().catch(() => {
      clearTimeout(playingTimer);
      videoEl.removeEventListener("playing", onPlaying);
      setNeedsResume(true);
    });
  }, []);

  useEffect(() => {
    const videoEl = playerRef.current;
    if (!videoEl || !tracks.length) {
      if (videoEl) {
        videoEl.srcObject = null;
        videoEl.style.transform = "none";
      }
      return;
    }

    tracksRef.current = tracks;
    const mediaStreamTracks = tracks.map((track) => track.getMediaStreamTrack());
    const stream = new MediaStream(mediaStreamTracks);
    streamRef.current = stream;
    videoEl.srcObject = stream;
    videoEl.style.setProperty("object-fit", objectFit, "important");

    if (mirror) {
      videoEl.style.transform = "rotateY(180deg)";
    } else {
      videoEl.style.transform = "none";
    }

    const onInitialPlaying = () => {
      clearTimeout(initialPlayTimer);
      setNeedsResume(false);
    };
    const initialPlayTimer = setTimeout(() => {
      videoEl.removeEventListener("playing", onInitialPlaying);
      setNeedsResume(true);
    }, 2000);
    videoEl.addEventListener("playing", onInitialPlaying, { once: true });
    videoEl.play().catch(() => {
      clearTimeout(initialPlayTimer);
      videoEl.removeEventListener("playing", onInitialPlaying);
      setNeedsResume(true);
    });

    const isCurrentlyFullscreen = () =>
      !!(document as any).fullscreenElement ||
      !!(document as any).webkitFullscreenElement ||
      !!(document as any).mozFullScreenElement ||
      !!(document as any).msFullscreenElement;

    const handleFullscreenChange = () => {
      const fs = isCurrentlyFullscreen();
      setIsFullscreenActive(fs);
      if (!fs) {
        setNeedsResume(true);
        setTimeout(() => resumeStream(objectFit), 400);
      }
    };

    const handleWebkitEndFullscreen = () => {
      // iOS: show overlay immediately, do NOT auto-resume — play() outside a user gesture is blocked on iOS
      setIsFullscreenActive(false);
      setNeedsResume(true);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);
    videoEl.addEventListener("webkitendfullscreen", handleWebkitEndFullscreen);

    const resizeObserver = new ResizeObserver(() => {
      if (!isCurrentlyFullscreen()) {
        videoEl.style.setProperty("object-fit", objectFit, "important");
      }
    });
    if (videoEl.parentElement) {
      resizeObserver.observe(videoEl.parentElement);
    }

    return () => {
      resizeObserver.disconnect();
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
      document.removeEventListener("MSFullscreenChange", handleFullscreenChange);
      videoEl.removeEventListener("webkitendfullscreen", handleWebkitEndFullscreen);
    };
  }, [tracks, mirror, objectFit, resumeStream]);

  useEffect(() => {
    const videoEl = playerRef.current;
    if (!videoEl) return;
    const id = `broadcast-native-${index}`;
    videoEl.id = id;
    if (isBlur) {
      videoEl.classList.add("blur");
    } else {
      videoEl.classList.remove("blur");
    }
  }, [isBlur, index]);

  const handleClickResume = () => {
    resumeStream(objectFit);
  };

  const showControlsTemporarily = () => {
    setControlsVisible(true);
    onControlsVisibilityChange?.(true);
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      setControlsVisible(false);
      onControlsVisibilityChange?.(false);
    }, 3000);
  };

  const handleTapInterceptor = (e: React.MouseEvent) => {
    // This div sits above the video — handles tap on the video area reliably on iOS
    e.stopPropagation();
    if (!needsResume) {
      showControlsTemporarily();
    }
  };

  const handleMuteToggle = () => {
    const videoEl = playerRef.current;
    if (!videoEl) return;
    videoEl.muted = !videoEl.muted;
    setIsMuted(videoEl.muted);
    showControlsTemporarily();
  };

  const handleFullscreenToggle = () => {
    const videoEl = playerRef.current;
    if (!videoEl) return;
    showControlsTemporarily();
    if (isFullscreenActive) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      }
    } else {
      if (videoEl.requestFullscreen) {
        videoEl.requestFullscreen();
      } else if ((videoEl as any).webkitEnterFullscreen) {
        (videoEl as any).webkitEnterFullscreen();
      } else if ((videoEl as any).webkitRequestFullscreen) {
        (videoEl as any).webkitRequestFullscreen();
      }
    }
  };

  const nativeVideo = (
    <video
      ref={playerRef}
      autoPlay
      playsInline
      muted
      className="broadcast-native-video"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        objectFit: objectFit,
      }}
    />
  );

  if (!showControls) {
    return <div className="broadcast-native-player">{nativeVideo}</div>;
  }

  const playerClasses = ["broadcast-native-player", controlsVisible || needsResume ? "controls-visible" : ""].filter(Boolean).join(" ");

  return (
    <div className={playerClasses}>
      {nativeVideo}
      {/* Transparent tap interceptor — sits above video to catch taps iOS video element swallows */}
      {!needsResume && <div onClick={handleTapInterceptor} style={{ position: "absolute", inset: 0, zIndex: 20, cursor: "pointer" }} />}

      {needsResume && (
        <div
          onClick={handleClickResume}
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            zIndex: 30,
          }}
        >
          <div>
            <img src={PlayIcon} width="60" height="60" alt="play" />
          </div>
        </div>
      )}

      <div className="broadcast-native-control-bar">
        <button className="broadcast-native-btn" onClick={handleFullscreenToggle} aria-label="Toggle fullscreen">
          <img src={FullScreenIcon} width="25" height="25" alt="fullscreen" />
        </button>
      </div>
    </div>
  );
};

export default BroadCastViPlayer;
